import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

interface AccessDeniedModalProps {
  isOpen: boolean;
  onClose: () => void;
  reason: 'permissions' | 'past_due';
}

const AccessDeniedModal: React.FC<AccessDeniedModalProps> = ({
  isOpen,
  onClose,
  reason
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleManageSubscription = () => {
    onClose();
    navigate('/subscription');
  };

  const getModalContent = () => {
    if (reason === 'permissions') {
      return {
        title: 'Feature Not Available',
        message: 'Your current account level does not have the ability to use this feature. Please upgrade your subscription to continue.'
      };
    } else {
      return {
        title: 'Subscription Past Due',
        message: 'Your subscription is currently past due. Please update your billing information to continue your subscription.'
      };
    }
  };

  const { title, message } = getModalContent();

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
        </ModalHeader>
        
        <ModalBody>
          <Message>{message}</Message>
        </ModalBody>

        <ModalFooter>
          <CancelButton onClick={onClose}>
            Cancel
          </CancelButton>
          <ManageButton onClick={handleManageSubscription}>
            Manage Subscription
          </ManageButton>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #333;
`;

const ModalTitle = styled.h2`
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const Message = styled.p`
  color: #ccc;
  font-size: 16px;
  line-height: 1.5;
  margin: 0;
`;

const ModalFooter = styled.div`
  padding: 16px 24px 24px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  &:hover {
    transform: translateY(-1px);
  }
`;

const CancelButton = styled(Button)`
  background: transparent;
  color: #888;
  border: 1px solid #444;

  &:hover {
    background: #333;
    color: #ccc;
  }
`;

const ManageButton = styled(Button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  }
`;

export default AccessDeniedModal;